/**
 * Estado de servidor dos habitos (spec 4.5 e 9.5).
 *
 * A escrita otimista aqui cobre o REGISTRO do dia, nao o streak. Marcar o
 * habito muda a bolinha na hora, que e o feedback que a pessoa esta esperando;
 * o numero ao lado chega na invalidacao seguinte.
 *
 * A alternativa — estimar o streak no cliente — exigiria o historico vitalicio
 * que so o servidor tem. Chutar o incremento daria certo quase sempre e erraria
 * exatamente no caso interessante (a marcacao que reata uma sequencia antiga),
 * mostrando um numero que depois se corrige sozinho na tela.
 */

import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  uuidv7,
  type CreateHabitPayload,
  type Habit,
  type HabitStatsResponse,
  type UpdateHabitPayload,
} from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { withOfflineQueue } from '../../lib/sync.js';

export const habitKeys = {
  all: ['habits'] as const,
  list: (includeArchived: boolean) => ['habits', 'list', includeArchived] as const,
  stats: ['habits', 'stats'] as const,
};

export function useHabits(includeArchived = false) {
  return useQuery({
    queryKey: habitKeys.list(includeArchived),
    queryFn: () => api.habits.list(includeArchived),
    staleTime: 60_000,
  });
}

export function useHabitStats() {
  return useQuery({
    queryKey: habitKeys.stats,
    queryFn: () => api.habits.stats(),
    // Curto porque o recorte depende de "hoje": manter a resposta em cache por
    // muito tempo faria a faixa continuar circulando ontem depois da meia-noite.
    staleTime: 30_000,
  });
}

export function newHabitId(): string {
  return uuidv7();
}

function useInvalidateHabits(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: habitKeys.all });
  }, [qc]);
}

export function useCreateHabit() {
  const invalidate = useInvalidateHabits();

  return useMutation({
    mutationFn: (payload: CreateHabitPayload) => api.habits.create(payload),
    onSuccess: invalidate,
  });
}

type HabitListSnapshot = Array<[QueryKey, { habits: Habit[] } | undefined]>;

/**
 * Aplica o patch nas duas listas em cache — ativos e "com arquivados".
 *
 * Sem isto, a edicao feita sem rede iria para a fila e a tela continuaria
 * mostrando o valor antigo ate a drenagem: o formulario fecharia e a pessoa
 * concluiria que a mudanca se perdeu, que e o oposto do que aconteceu.
 *
 * `archived` decide a PRESENCA na lista de ativos, nao so um campo: arquivar
 * tira de la, e desarquivar traz de volta o registro que so a lista com
 * arquivados tinha.
 */
function patchHabitCaches(
  qc: QueryClient,
  id: string,
  patch: UpdateHabitPayload,
): HabitListSnapshot {
  const snapshot = qc.getQueriesData<{ habits: Habit[] }>({ queryKey: ['habits', 'list'] });

  const atual = snapshot
    .flatMap(([, data]) => data?.habits ?? [])
    .find((h) => h.id === id);
  if (!atual) return snapshot;

  const alterado: Habit = { ...atual, ...(patch as Partial<Habit>) };

  for (const [key, data] of snapshot) {
    if (!data) continue;
    const incluiArquivados = key[2] === true;
    const outros = data.habits.filter((h) => h.id !== id);
    const fica = incluiArquivados || !alterado.archived;

    qc.setQueryData<{ habits: Habit[] }>(key, {
      ...data,
      habits: fica
        ? [...outros, alterado].sort(
            (a, b) => Number(a.archived) - Number(b.archived) || a.createdAt - b.createdAt,
          )
        : outros,
    });
  }

  return snapshot;
}

/**
 * Editar, arquivar e desarquivar — com a fila offline, como as tarefas.
 *
 * O servidor ja aceitava `habit/update` no `/sync`; so o cliente chamava o
 * PATCH direto, e sem rede a edicao falhava em vez de esperar.
 */
export function useUpdateHabit() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHabits();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateHabitPayload }) =>
      withOfflineQueue({ entity: 'habit', action: 'update', entityId: id, payload: patch }, () =>
        api.habits.update(id, patch),
      ),

    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['habits', 'list'] });
      return patchHabitCaches(qc, id, patch);
    },

    onError: (_erro, _variaveis, snapshot) => {
      for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
    },

    onSettled: invalidate,
  });
}

export function useDeleteHabit() {
  const invalidate = useInvalidateHabits();

  return useMutation({
    mutationFn: (id: string) => api.habits.remove(id),
    onSuccess: invalidate,
  });
}

export interface LogHabitVariables {
  habit: Habit;
  date: string;
  value: number;
}

/**
 * Marca ou desmarca o dia.
 *
 * Desmarcar envia `value: 0` em vez de apagar o registro: "explicitamente nao
 * fiz" e um fato diferente de "nunca respondi", e apagar perderia a nota.
 */
export function useLogHabit() {
  const qc = useQueryClient();

  return useMutation({
    // `entityId` e o id do HABITO: o registro diario se identifica por
    // (habito, data), a mesma chave que torna o upsert idempotente na drenagem.
    mutationFn: ({ habit, date, value }: LogHabitVariables) =>
      withOfflineQueue(
        { entity: 'habit_log', action: 'create', entityId: habit.id, payload: { date, value } },
        () => api.habits.log(habit.id, { date, value }),
      ),

    onMutate: async ({ habit, date, value }) => {
      await qc.cancelQueries({ queryKey: habitKeys.stats });
      const anterior = qc.getQueryData<HabitStatsResponse>(habitKeys.stats);
      if (!anterior) return { anterior };

      qc.setQueryData<HabitStatsResponse>(habitKeys.stats, {
        ...anterior,
        logs: [
          ...anterior.logs.filter((l) => !(l.habitId === habit.id && l.logDate === date)),
          // Id provisorio: o servidor gera o definitivo. Ele so precisa ser
          // unico dentro deste array ate a invalidacao chegar.
          {
            id: `optimistic:${habit.id}:${date}`,
            habitId: habit.id,
            logDate: date,
            value,
            note: null,
          },
        ],
      });

      return { anterior };
    },

    onError: (_erro, _variaveis, context) => {
      if (context?.anterior) qc.setQueryData(habitKeys.stats, context.anterior);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: habitKeys.all });
    },
  });
}
